import { useState } from "react"
import { ANALYTICS_EVENTS, contactSubmitErrorKind, track } from "@/lib/analytics"
import type { ContactFormData } from "@/lib/contact-form-schema"
import { contactSubmitErrorMessage } from "@/lib/contact-intake"

interface UseContactFormReturn {
  isSubmitting: boolean
  isSuccess: boolean
  error: string | null
  submitForm: (data: ContactFormData) => Promise<void>
  resetForm: () => void
}

export function useContactForm(): UseContactFormReturn {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submitForm = async (data: ContactFormData) => {
    setIsSubmitting(true)
    setError(null)
    setIsSuccess(false)

    // Set once the failure has already been reported, so the catch below does
    // not report the same submission a second time as a network failure.
    let errorTracked = false

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      })

      const result = await response.json()

      if (!response.ok) {
        track(ANALYTICS_EVENTS.contactFormSubmitError, {
          kind: contactSubmitErrorKind(response.status),
          status: response.status,
        })
        errorTracked = true
        throw new Error(result.error || "Failed to send message")
      }

      setIsSuccess(true)
      track(ANALYTICS_EVENTS.contactFormSubmitSuccess)
    } catch (err) {
      if (!errorTracked) {
        track(ANALYTICS_EVENTS.contactFormSubmitError, { kind: "network" })
      }
      setError(
        err instanceof Error
          ? err.message
          : contactSubmitErrorMessage({
              ok: false,
              code: "delivery",
              message: "An unexpected error occurred",
            })
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setIsSuccess(false)
    setError(null)
  }

  return {
    isSubmitting,
    isSuccess,
    error,
    submitForm,
    resetForm,
  }
}
